/**
 * InquiryListContainer
 * --
 */
import React, { useState } from 'react';
import InquiryListPresenter from './InquiryListPresenter';
import {
  useCreateInquiry,
  useDeleteInquiry,
  useGetInquiries,
  useUpdateInquiry,
} from 'hooks/InquiryHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useGetAdmins } from 'hooks/AdminHooks';
import { useUserStack } from 'store';
import Logger from 'utils/logger';
import MessageAlert from 'utils';
import Alert from 'utils/AlertManager';

const logger = new Logger('InquiryListContainer');

/**----------------------------------------
 * [Component] 총관리자: 문의목록 - 컨테이너
 * ----------------------------------------
 */
const InquiryListContainer = ({ defaultShopId, permission, FAQ = false }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentShop, setCurrentShop] = useState(defaultShopId ?? null);

  /* ===== Mutate ===== */
  const { createInquiry } = useCreateInquiry();
  const { updateInquiry } = useUpdateInquiry();
  const { deleteInquiry } = useDeleteInquiry();

  /* ===== Data ===== */
  const userInfo = useUserStack();
  const { data: ShopInformations } = useGetShopInformations(
    {},
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        const filtered = data.filter((item) => item.type !== 'STORE');

        return filtered;
      },
      initialData: [],
    },
  );
  const { data: Admins } = useGetAdmins(
    {},
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      initialData: [],
    },
  );
  const { data: Inquiries, refetch } = useGetInquiries(
    {
      join_tables: `["user", "product", "inquiry_file", "admin", "shop_information"]`,
      // ...(currentShop && { shop_information_id: currentShop }),
      ...(FAQ && { inquiry_type: 'FAQ' }),
    },
    {
      enabled: FAQ ? true : !!currentShop,
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];

        return data;
      },
      initialData: [],
    },
  );
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 문의 생성/수정 유효성 검사
   * --
   * @param {Object} d
   * @returns
   */
  const handleValidateCheck = (d) => {
    const {
      admin_id,
      shop_information_id,
      inquiry_sub_type,
      inquiry_name,
      inquiry_content,
    } = d;

    if (!admin_id) {
      MessageAlert.warning('로그인후 다시 시도해주세요!');
      return false;
    }

    if (!shop_information_id) {
      MessageAlert.warning('업체을 선택해주세요!');
      return false;
    }

    if (!inquiry_sub_type) {
      MessageAlert.warning('문의유형을 선택해주세요!');
      return false;
    }

    if (!inquiry_name) {
      MessageAlert.warning('문의제목을 입력해주세요!');
      return false;
    }

    if (!inquiry_content) {
      MessageAlert.warning('문의내용을 입력해주세요!');
      return false;
    }

    return true;
  };

  /**
   * 문의 생성
   * @param {Object} d
   * @returns
   */
  const handleCreateInquiry = async (d = null) => {
    if (!handleValidateCheck(d)) return false;

    if (!Alert.confirm('문의하시겠습니까?')) return false;

    let isSuccess = false;

    try {
      setLoading(true);

      const mutate = {
        ...d,
      };
      const { status, message } = await createInquiry(mutate);

      if (status !== 200) {
        logger.warn('handleCreateInquiry', message);
        MessageAlert.warning('문의하기에 실패하였습니다.');
        isSuccess = false;
        return false;
      }

      MessageAlert.success('성공적으로 문의하였습니다.');
      refetch();
      isSuccess = true;
      return isSuccess;
    } catch (err) {
      logger.error('handleCreateInquiry', err);
      MessageAlert.error('문의하기 에러');
      return false;
    } finally {
      setTimeout(() => {
        setLoading(false);
        return isSuccess;
      }, 1000);
    }
  };

  /**
   * 문의 수정
   * --
   * @param {Object} d
   */
  const handleUpdateInquiry = async (d = null) => {
    if (!d.inquiry_id) {
      MessageAlert.info('문의 삭제 실패!');
      return false;
    }

    if (!handleValidateCheck(d)) return false;

    if (!Alert.confirm('문의를 수정하시겠습니까?')) return false;

    try {
      setLoading(true);

      const mutate = {
        ...d,
      };

      const { status, message } = await updateInquiry(mutate);

      if (status !== 200) {
        logger.warn('handleUpdateInquiry', message);
        MessageAlert.warning('문의 수정에 실패하였습니다.');
        return false;
      }

      MessageAlert.success('성공적으로 문의를 수정하였습니다.');
      refetch();
      return true;
    } catch (err) {
      logger.error('handleUpdateInquiry', err);
      MessageAlert.error('문의 수정 에러');
      return false;
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  /**
   * 문의 삭제
   * --
   * @param {Object} d
   */
  const handleDeleteInquiry = async (d = null) => {
    const { inquiry_id, inquiry_answer } = d;

    if (inquiry_answer && permission !== 'ADMIN') {
      MessageAlert.info('답변이 작성된 문의는 삭제할 수 없습니다.');
      return false;
    }

    if (!inquiry_id) {
      MessageAlert.info('문의 삭제 실패!');
      return false;
    }

    if (!Alert.confirm('문의를 삭제하시겠습니까?\n삭제 후 복구할 수 없습니다.'))
      return false;

    try {
      const { status, message } = await deleteInquiry(inquiry_id);

      if (status !== 200) {
        logger.warn('handleDeleteInquiry', message);
        MessageAlert.warning('문의 삭제에 실패하였습니다.');
        return false;
      }

      MessageAlert.success('성공적으로 문의를 삭제하였습니다.');
      refetch();
      return true;
    } catch (err) {
      logger.error('handleDeleteInquiry', err);
      MessageAlert.error('문의 삭제 에러');
      return false;
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  /**--------------------
   * 문의삭제
   * --------------------
   */
  const handleDeletedInquiry = async (key) => {
    setData(data.filter((item) => item.inquiry_id !== key));
  };

  /* ===== HOOKS ===== */
  const InquiryList = React.useMemo(() => {
    let newInquiryList = [];

    if (FAQ) {
      newInquiryList = Inquiries;
    } else {
      if (!Inquiries.length || !ShopInformations.length || !Admins.length)
        return [];

      const shopMap = new Map(
        ShopInformations.map((item) => [item.shop_information_id, item]),
      );
      const adminMap = new Map(Admins.map((item) => [item.admin_id, item]));

      newInquiryList = Inquiries.map((inquiry) => {
        // 문의보낸 매장정보
        const sentShop =
          shopMap.get(inquiry?.admin?.shop_information_id) ?? null;
        // 답변 작성 관리자 정보
        const answerAdmin = adminMap.get(inquiry.inquiry_answer_id) ?? null;
        // 답변 작성 매장 정보
        const answerShop =
          shopMap.get(answerAdmin?.shop_information_id) ?? null;

        return {
          ...inquiry,
          sentShop,
          answerAdmin,
          answerShop,
          admins: Admins.filter(
            (item) =>
              item.shop_information_id === sentShop?.shop_information_id,
          ),
        };
      });
    }
    return newInquiryList;
  }, [Inquiries, ShopInformations, Admins, FAQ]);

  React.useEffect(() => {
    if (!currentShop && !FAQ) return;
    const newList = FAQ
      ? InquiryList
      : InquiryList.filter(
          (item) =>
            item.shop_information_id === currentShop ||
            item.admins.some(
              (sub) => sub.shop_information_id === currentShop,
            ) ||
            (!item.shop_information_id &&
              item.admins.length === 0 &&
              currentShop === 1 &&
              item.inquiry_type === 'ONEINQUIRY'),
        );
    setData(newList);
  }, [currentShop, InquiryList, FAQ]);

  /* ===== RENDER ===== */
  return (
    <InquiryListPresenter
      {...{
        FAQ,
        loading,
        data,
        permission,
        currentShop,
        setCurrentShop,
        onSavedInquiry: refetch,
        onDeletedInquiry: handleDeletedInquiry,
        setLoading,
        shops: ShopInformations,
        userInfo: userInfo,
        onCreate: handleCreateInquiry,
        onUpdate: handleUpdateInquiry,
        onDelete: handleDeleteInquiry,
      }}
    />
  );
};

/* DF */
InquiryListContainer.defaultProps = {
  defaultShopId: null,
  permission: 'ADMIN',
  FAQ: false,
};
export default InquiryListContainer;
